import React from 'react';
import { Redirect, useLocation } from 'react-router';
import { Spinner, SpinnerSize } from '@fluentui/react';
import { pageDataService } from 'common';
import { lazyLoad } from 'common/utilities/LazyLoad';
import { breachIdQueryParamName } from 'RouteConstants';
import { buildItmMaintenancePath, identityOnboardingPath } from 'Routes';
import { useIdentityOnboardingStatus } from 'store';
import { useWebView } from 'store/webView/useWebView';

// This page handles the following scenarios
// 1. landing page route (/identity)
//    a. default landing page
//    b. bulkAdd dialog (/identity/?showBulkAdd=true) - 'add more info' web link from email
// 2. email notification
//    a. single breach for a member (/identity/?breachId={}&csid={}&...)
//    b. multiple breaches for a member (/identity/?csid={}&...)
// 3. webview
//    a. landing page
//    b. take action from webview ITP landing page
//    c. alert page
//    d. push notification
//    e. email notification urls (single & multiple breaches) -> open in webview with protocol handler

const Loading = () => <Spinner size={SpinnerSize.large} style={{ flex: 1 }} />;

export const AsyncWebViewRecommendationDialog = lazyLoad(() => import('./webView/WebViewRecommendationDialog'), {
  fallback: <Loading />,
});

export const AsyncIdentityLandingPage = lazyLoad(() => import('./IdentityLandingPage'), {
  fallback: <Loading />,
});

export const IdentityProxyPage: React.FC = () => {
  const { isWebView } = useWebView();
  const query = new URLSearchParams(useLocation().search);
  const breachId = query.get(breachIdQueryParamName);
  const { itmOnboarded: isCurrentUserITMOnboarded } = useIdentityOnboardingStatus(pageDataService.getUserData().csid);

  if (isWebView && breachId) {
    return <AsyncWebViewRecommendationDialog />;
  }

  if (pageDataService.isItmMaintenanceMode()) {
    return <Redirect to={buildItmMaintenancePath('itm', true)} />;
  }

  const isMemberView = window.location.href.toLowerCase().includes('csid=');
  // Redirect to onboarding unless user is accessing member view
  if (!isWebView && !isCurrentUserITMOnboarded && !isMemberView) {
    return <Redirect to={identityOnboardingPath} />;
  }

  return <AsyncIdentityLandingPage />;
};

export default IdentityProxyPage;
